import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import type { FacilityKey, ParkingType } from '../types/ocpi'

/**
 * Hook that provides localized labels for OCPI facility types.
 *
 * @returns A memoized function that takes a FacilityKey and returns its translated label
 *
 * @example
 * ```tsx
 * const getFacilityLabel = useFacilityLabels()
 * const label = getFacilityLabel('HOTEL') // Returns translated label for hotel facility
 * ```
 */
export function useFacilityFormatter() {
  const t = useTranslations('data.ocpi')
  return useCallback((key: FacilityKey) => t(`facilities_${key}`), [t])
}

/**
 * Hook that provides localized labels for OCPI parking types.
 *
 * @returns A memoized function that takes a ParkingType and returns its translated label
 *
 * @example
 * ```tsx
 * const getParkingTypeLabel = useParkingTypeLabels()
 * const label = getParkingTypeLabel('ON_STREET') // Returns translated label for on-street parking
 * ```
 */
export function useParkingTypeFormatter() {
  const t = useTranslations('data.ocpi')
  return useCallback((key: ParkingType) => t(`parkingTypes_${key}`), [t])
}
